// src/encounter/structured-note/section-completion.ts
//
// TS-3 · frame v2 only. When an S·O·A·P section shows its ✓: only when it holds DOCUMENTED content and
// no required item in it is still blank — never because it was visited (authority §4 rule 8: "Visiting
// completes nothing"; §6 lists "a step shows ✓ on visit" as a known gap). In one continuous scroll every
// section is on screen at once, so "visited" means nothing at all; the old leave-a-section rule would
// tick everything the moment the note opened.
//
// Rule 8 names three states — documented / explicitly not applicable / unresolved required blanks. No
// "not applicable" control exists yet (a gap routed to the encounter owner), so a section is done when:
//   · at least one of its steps is documented, and
//   · no questionnaire step in it has an open required item (the same `openRequired` the Sign list uses).
// A step is documented when:
//   · HPI / Risk / ROS / PE: the clinician recorded an answer in that step's part of the template — on an
//     item that is enabled (enableWhen), not read-only, not calculated and not a static line, the same
//     items the Sign list asks about (`false` and 0 count: "Denies" is documentation; '' and [] do not).
//     A readout, a copied value or a score is never documentation (Codex P2, TS-3 round 1);
//   · A/P, per part (TS-3, A and P separate): A is documented by a diagnosis or an assessment narrative;
//     P by an order or an education article. Without a part (one A&P), any of the four.
// Doc, Wrap-up and the allied-health Plan carry nothing this view can read as documentation, so they
// never complete a section on their own. The nurse's "Nursing plan" (Wrap-up only) and the allied-health
// "Plan" therefore never show ✓ until a plan backend exists — honest, and routed as a gap.
import type { Questionnaire, QuestionnaireResponse } from '@scripsteam/scrips-signal-ds';
import type { PaStepKey } from '../lib/encounter-steps';
import { isQuestionnaireStep, questionnaireForStep } from '../lib/questionnaire-groups';
import { sectionsFor, type ApPart, type SoapSectionId } from '../lib/soap-ribbon';
import { STATIC_LINE_TYPE } from '../lib/readout-lines';
import { answerMap, enabledLeaves, isAnswered, openRequired } from './note-adapter';

/** What the A/P currently holds (the A/P props the view already receives; nothing is fetched). */
export interface PlanContent {
  diagnoses?: readonly unknown[];
  orders?: readonly unknown[];
  articles?: readonly unknown[];
  assessmentNote?: string;
}

export interface SectionCompletionInput {
  /** The nav the ribbon draws (role-resolved, empty sections hidden). */
  nav: readonly PaStepKey[];
  questionnaire?: Questionnaire;
  response: QuestionnaireResponse;
  plan?: PlanContent;
}

/** Is `step` documented, by the rule above? `part`: which part of the A/P step is being asked about. */
export function stepDocumented(step: PaStepKey, input: Omit<SectionCompletionInput, 'nav'>, part?: ApPart): boolean {
  const { questionnaire, response, plan } = input;
  if (isQuestionnaireStep(step)) {
    if (!questionnaire) return false;
    const values = answerMap(response);
    return enabledLeaves(questionnaireForStep(questionnaire, step).item, values).some(
      (it) => it.type !== STATIC_LINE_TYPE && !it.readOnly && !it.calculatedExpression && isAnswered(values[it.linkId]),
    );
  }
  if (step === 'ap') {
    const assessed = (plan?.diagnoses?.length ?? 0) > 0 || (plan?.assessmentNote ?? '').trim().length > 0;
    const planned = (plan?.orders?.length ?? 0) > 0 || (plan?.articles?.length ?? 0) > 0;
    if (part === 'assessment') return assessed;
    if (part === 'plan') return planned;
    return assessed || planned;
  }
  return false;
}

/** The sections that show ✓: documented, with no open required blank. */
export function completedSections(input: SectionCompletionInput): Set<SoapSectionId> {
  const { nav, questionnaire, response } = input;
  const values = answerMap(response);
  const done = new Set<SoapSectionId>();
  for (const section of sectionsFor(nav)) {
    const documented = section.items.some((step) => stepDocumented(step, input, section.apPart));
    if (!documented) continue;
    const blank = section.items.some(
      (step) => isQuestionnaireStep(step) && !!questionnaire && openRequired(questionnaireForStep(questionnaire, step), values).length > 0,
    );
    if (!blank) done.add(section.id);
  }
  return done;
}
